/**
 * Canvas Node Model Configuration Schemas
 *
 * Database-ready schemas for AI model configurations per node type.
 * Designed to be stored in DB and managed via admin panel in the future.
 */

import { z } from 'zod';

// =============================================================================
// Setting Types
// =============================================================================

/**
 * Base setting schema
 */
const BaseSettingSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string().optional(),
  required: z.boolean().default(false),
});

/**
 * Number input setting (iterations, max tokens, etc.)
 */
export const NumberSettingSchema = BaseSettingSchema.extend({
  type: z.literal('number'),
  default: z.number(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
});

/**
 * Slider setting (temperature, creativity, guidance scale)
 */
export const SliderSettingSchema = BaseSettingSchema.extend({
  type: z.literal('slider'),
  default: z.number(),
  min: z.number(),
  max: z.number(),
  step: z.number(),
  displayDecimals: z.number().optional(),
});

/**
 * Select dropdown setting (model variant, image size)
 */
export const SelectSettingSchema = BaseSettingSchema.extend({
  type: z.literal('select'),
  default: z.string(),
  options: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      description: z.string().optional(),
    })
  ),
});

/**
 * Text input setting (system prompt, seed)
 */
export const TextSettingSchema = BaseSettingSchema.extend({
  type: z.literal('text'),
  default: z.string(),
  placeholder: z.string().optional(),
  multiline: z.boolean().optional(),
});

/**
 * Switch/toggle setting (enable feature, etc.)
 */
export const SwitchSettingSchema = BaseSettingSchema.extend({
  type: z.literal('switch'),
  default: z.boolean(),
});

/**
 * Union of all setting types
 */
export const NodeSettingSchema = z.discriminatedUnion('type', [
  NumberSettingSchema,
  SliderSettingSchema,
  SelectSettingSchema,
  TextSettingSchema,
  SwitchSettingSchema,
]);

export type NodeSetting =
  | z.infer<typeof NumberSettingSchema>
  | z.infer<typeof SliderSettingSchema>
  | z.infer<typeof SelectSettingSchema>
  | z.infer<typeof TextSettingSchema>
  | z.infer<typeof SwitchSettingSchema>;

// =============================================================================
// Model Configuration
// =============================================================================

/**
 * AI Provider types
 */
export const AIProviderSchema = z.enum(['fal', 'openrouter', 'genie']);
export type AIProvider = z.infer<typeof AIProviderSchema>;

/**
 * Node types that support AI models
 */
export const AINodeTypeSchema = z.enum(['text-llm', 'image', 'video', '3d', 'sound', 'shader']);
export type AINodeType = z.infer<typeof AINodeTypeSchema>;

/**
 * Settings grouped by section
 */
export const NodeModelSettingsSectionsSchema = z.object({
  basic: z.array(NodeSettingSchema).default([]),
  style: z.array(NodeSettingSchema).default([]).optional(),
  advanced: z.array(NodeSettingSchema).default([]).optional(),
});

export type NodeModelSettingsSections = z.infer<typeof NodeModelSettingsSectionsSchema>;

/**
 * Single model configuration
 */
export const NodeModelConfigSchema = z.object({
  id: z.string(), // Unique identifier (e.g., "fal-flux-pro", "flux-2-flash")
  endpointId: z.string().optional(), // Provider endpoint ID (e.g., "fal-ai/flux-pro/v1.1")
  name: z.string(), // Display name (e.g., "Mock Model", "ChatGPT 4o", "Fal Flux Pro")
  provider: AIProviderSchema,
  enabled: z.boolean().default(true),
  isDefault: z.boolean().default(false),
  description: z.string().optional(),
  // Parameter schema from provider (OpenAPI/fal.ai)
  parameterSchema: z.any().optional(), // JSON Schema object
  // Admin overrides for parameters (visibility, defaults, UI type, input/output config)
  parameterOverrides: z.record(z.string(), z.any()).optional().nullable(), // ModelParameterOverrides
  nodeType: AINodeTypeSchema, // Node type association (required)
  // Model taxonomy fields
  modelFamilySlug: z.string().optional(), // Family slug (flux, stable-diffusion, gpt, etc.)
  modelFamily: z.string().optional(), // Family display name (Flux, Stable Diffusion, GPT, etc.)
  familyVersion: z.string().optional(), // Version within family (e.g., "pro", "1.1", "ultra")
  qualityTier: z.string().optional(), // Quality tier (e.g., "standard", "hd", "ultra")
  featuredScore: z.number().optional(), // Sort priority (higher = first)
  purpose: z.string(), // Model purpose (create, edit, enhance) — required
  subPurpose: z.string().optional(), // Sub-purpose (upscale, inpaint, etc.)
});

export type NodeModelConfig = z.infer<typeof NodeModelConfigSchema>;

/**
 * Model configuration for a specific node type
 */
export const NodeTypeModelConfigSchema = z.object({
  nodeType: AINodeTypeSchema,
  models: z.array(NodeModelConfigSchema),
});

export type NodeTypeModelConfig = z.infer<typeof NodeTypeModelConfigSchema>;

/**
 * Complete model configuration registry
 */
export const ModelConfigRegistrySchema = z.array(NodeTypeModelConfigSchema);
export type ModelConfigRegistry = z.infer<typeof ModelConfigRegistrySchema>;

// =============================================================================
// Runtime Setting Values
// =============================================================================

/**
 * User's selected settings for a model instance
 */
export const NodeModelSettingsSchema = z.record(z.string(), z.any());
export type NodeModelSettings = z.infer<typeof NodeModelSettingsSchema>;

/**
 * Selected model + settings for a node instance
 */
export const NodeModelSelectionSchema = z.object({
  modelId: z.string(),
  settings: NodeModelSettingsSchema,
});

export type NodeModelSelection = z.infer<typeof NodeModelSelectionSchema>;

// =============================================================================
// Helper Types
// =============================================================================

/**
 * Extract setting value type from setting
 */
export type SettingValue<T extends NodeSetting> = T extends { default: infer D } ? D : never;

/**
 * Convert setting array to typed object
 */
export type SettingsObject<T extends readonly NodeSetting[]> = {
  [K in T[number] as K['key']]: SettingValue<K>;
};
